import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { loadUiComponent } from "../graphql/queries/loadUiComponent";
import { OUTLET_METADATA_SCHEMA_SLUG } from "../components/campaign/rulesAndOutcomes/outcomes/storeOutcome/storeOutcomeUtils";

/**
 * Loads the OUTLET metadata schema `ui_component` (slug `outlet-metadata-schema`,
 * seeded per program in admin-database-settings) via the generic `loadUiComponent`
 * query. Its `ui_schema.fields` are the single source of truth for the store table
 * columns, the persisted `store_configuration` snapshot, and which paths are
 * filterable — replacing the previously hardcoded field list.
 *
 * Each field is `{ path, label, type, filterable }`. `filterable: true` paths back the
 * metadata filter bar and map to the admin-bff `metadataFilters` contract (ADO 26708962).
 *
 * The schema is required: when it is absent/empty (`isConfigured === false`) the
 * store-condition wizard shows an unconfigured state and blocks the flow.
 *
 * @returns {{
 *   fields: Array<{path: string, label: string, type: string, filterable: boolean}>,
 *   filterableFields: Array,
 *   keywordGroupType: string | undefined,
 *   isConfigured: boolean,
 *   loading: boolean,
 *   error: object,
 * }}
 */
export const useOutletMetadataSchema = () => {
  const { data, loading, error } = useQuery(loadUiComponent, {
    variables: { slug: OUTLET_METADATA_SCHEMA_SLUG },
  });

  const uiSchema = data?.loadUiComponent?.uiSchema;

  const fields = useMemo(
    () => (Array.isArray(uiSchema?.fields) ? uiSchema.fields : []),
    [uiSchema]
  );

  const filterableFields = useMemo(
    () => fields.filter((field) => field.filterable),
    [fields]
  );

  return {
    fields,
    filterableFields,
    keywordGroupType: uiSchema?.keywordGroupType,
    isConfigured: fields.length > 0,
    loading,
    error,
  };
};
